/*
 * Layout  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FunctionComponent, useCallback, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { isString } from "@adapptio/lib-shared";
import { LayoutState_Panel } from "./LayoutState";
import { TLayoutActivateHandler, TLayoutRenderComponentFn } from "./LayoutTypes";
import { useLayoutElement } from "./useLayoutElement";
import { Toolbar } from "../Toolbar/Toolbar";
import { LayoutResizableElement } from "./LayoutResizableElement";
import { LayoutContextProvider, useLayoutContext } from "./LayoutContext";
import { renderLayoutElement } from "./renderLayoutElement";

export interface ILayoutPanelProps {
	state: LayoutState_Panel;
	renderComponent: TLayoutRenderComponentFn;
	onActivate?: TLayoutActivateHandler;
}

export const LayoutPanel: FunctionComponent<ILayoutPanelProps> = ({ state, renderComponent, onActivate }) => {
	const el = useLayoutElement(state);
	const layoutElRef = useRef<HTMLDivElement>();
	const { orientation } = useLayoutContext();
	const isExpanded = !el.isExpandable || (el.isExpandable && el.isExpanded);
	const isHidden = el.isHideable && el.isHidden;
	const renderWhenHidden = el.renderWhenHidden;

	const sizeProperty = orientation.vertical ? "height" : "width";

	const handleFocus = useCallback(() => {
		if (onActivate) {
			onActivate(el);
		}
	}, [ onActivate ]);

	const handleToggleExpanded = useCallback(() => {
		el.toggleExpanded();
	}, [ el ]);

	const baseClass = BEM("edt-layout", "element", {
		"type-panel": true,
		"has-toolbar": !!el.toolbarComponent,
		"is-expandable": el.isExpandable,
		"is-expanded": el.isExpandable && el.isExpanded,
		"is-collapsed": el.isExpandable && !el.isExpanded,
		"is-hideable": el.isHideable,
		"is-hidden": el.isHidden
	});

	baseClass.add("m-layout-panel-" + el.id);

	const style: React.CSSProperties = {};

	if (isString(el.size)) {
		style[sizeProperty] = el.size;
	} else {
		style.flex = `${el.size} 1 0%`;
	}

	if (isHidden && !renderWhenHidden) {
		return null;
	}

	return (
		<LayoutResizableElement
			state={state}
			className={baseClass.toClassName()}
			style={style}
			onFocus={handleFocus}
			tabIndex={0}
			ref={layoutElRef}
		>
			<LayoutContextProvider state={state} layoutElRef={layoutElRef}>
				{el.toolbarComponent || el.isExpandable ? (
					<div className={baseClass.element("element-panel-toolbar").toClassName()}>
						<Toolbar
							expandable={el.isExpandable}
							expanded={el.isExpanded}
							onToggleExpanded={handleToggleExpanded}
							arrowPosition={el.arrowPosition}
						>
							{el.toolbarComponent ? renderComponent(el.toolbarComponent) : null}
						</Toolbar>
					</div>
				) : null}
				{el.secondaryToolbarComponent && (
					<div
						className={baseClass.element("element-panel-toolbar").mod("secondary").toClassName()}
					>
						<Toolbar>{renderComponent(el.secondaryToolbarComponent)}</Toolbar>
					</div>
				)}
				{el.children?.length > 0
					? el.children.map((child) => renderLayoutElement(child, renderComponent, onActivate))
					: null}
				{el.contentComponent && (
					<div className={baseClass.element("element-panel-content").toClassName()}>
						{isExpanded || (!isExpanded && el.renderWhenCollapsed !== false)
							? renderComponent(el.contentComponent)
							: null}
					</div>
				)}
			</LayoutContextProvider>
		</LayoutResizableElement>
	);
};
